/**
 * Prepaid coverage grid — staff-facing list of prepaid cellular coverage records.
 *
 * Server-backed via EntityGrid over odl-app/prepaid-coverages/. Placement and
 * location fields are computed client-side from the location object. Supports
 * multi-select with Convert, Edit, and Renew dialogs (mutations stay on the
 * legacy /billing/prepaid-coverages/ endpoints — the odl-app list is read-only).
 * A CSV export pages the entire filtered set.
 *
 * Props:
 *  - customerId    — optional; scopes list to a single customer
 *  - onCustomerChange — when provided, a Customer toolbar filter renders and
 *    reports selection changes (fixed-scope hosts omit it and pass customerId)
 *  - statusFilter  — controlled status filter value (falls back to internal state)
 *  - onStatusFilterChange — callback when status filter changes (controlled mode)
 *  - onDataChange  — callback fired after any successful mutation (e.g. to refresh parent summary cards)
 *
 * Contains:
 *  - PrepaidCoverageGrid — default export
 */

import EditIcon from '@mui/icons-material/Edit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { addMonths } from 'date-fns';
import { Form, Formik } from 'formik';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import * as Yup from 'yup';

import { fetchPrepaidCoveragesGrid } from '@sophocles/api/billing';
import { fetchCustomer } from '@sophocles/api/customer';
import { useUser } from '@sophocles/app';
import FilterBar from '@sophocles/components/FilterBar';
import ProductAutocomplete from '@sophocles/components/ProductAutocomplete';
import ScopeSearchInput from '@sophocles/components/ScopeSearchInput';
import { customerScopeChip, customerScopeProvider } from '@sophocles/components/ScopeSearchInput/scopeProviders';
import UserAutoComplete from '@sophocles/components/UserAutoComplete';
import { EntityGrid, ReferenceChip, useDataGridConfig } from '@sophocles/data-grids';
import GridStatusCell from '@sophocles/data-grids/components/cells/GridStatusCell';
import useGridPreferenceKey from '@sophocles/data-grids/datasource/useGridPreferenceKey';
import useGridQuickFilter from '@sophocles/data-grids/datasource/useGridQuickFilter';
import useGridRefreshRef from '@sophocles/data-grids/datasource/useGridRefreshRef';
import { fetchAllUserSellers } from '@sophocles/entities/User/userActions';
import { useCreatePrepaidCoveragePlanMutation } from '@sophocles/features/entities/prepaidCoverages';
import { refLabel } from '@sophocles/features/entities/references';
import { addToast } from '@sophocles/features/toast';
import useUserPreference from '@sophocles/hooks/useUserPreference';
import SalesProvider from '@sophocles/providers/SalesProvider';
import { formatDate } from '@sophocles/utils/formatUtils';

import { EditCoverageDialog, ConvertCoverageDialog } from '../dialogs/PrepaidCoverageDialogs';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUS_CHIP_COLOR = {
  active: 'ok',
  expiring: 'warning',
  expired: 'bad',
  scheduled: 'info',
  converted: 'attention',
};

const getStatusOptions = t => [
  { value: '', label: t('billing.prepaidCoverage.statusAll') },
  { value: 'active', label: t('billing.prepaidCoverage.statusActive') },
  { value: 'expiring', label: t('billing.prepaidCoverage.statusExpiring') },
  { value: 'scheduled', label: t('billing.prepaidCoverage.statusScheduled') },
  { value: 'expired', label: t('billing.prepaidCoverage.statusExpired') },
  { value: 'converted', label: t('billing.prepaidCoverage.statusConverted') },
];

const getOwnershipLabels = t => ({
  fixed_owner: t('billing.prepaidCoverage.ownershipFixed'),
  follows_radio: t('billing.prepaidCoverage.ownershipFollowsRadio'),
});

const getSourceLabels = t => ({
  order_sale_item: t('billing.prepaidCoverage.sourceOrderSaleItem'),
  migration: t('billing.prepaidCoverage.sourceMigration'),
  manual: t('billing.prepaidCoverage.sourceManual'),
  transfer: t('billing.prepaidCoverage.sourceTransfer'),
});

// ---------------------------------------------------------------------------
// Row normalization — computes placement and location display fields from the
// nested `location` object returned by the API. These computed fields are then
// available in fieldOverrides renderCells and in the CSV export.
// ---------------------------------------------------------------------------
function buildNormalizeRows(t) {
  return function normalizeRows(inputRows) {
    return inputRows.map(row => {
      const loc = row.location;
      const hasSite = !!loc?.site;
      const hasSiteGroup = !!loc?.site_group;
      const hasCustomer = !!loc?.customer;
      const hasInventoryLocation = !!loc?.inventory_location;

      const placementType = hasSite
        ? 'onSite'
        : hasSiteGroup
          ? 'plantInventory'
          : hasCustomer
            ? 'customerInventory'
            : hasInventoryLocation
              ? 'inventoryLocation'
              : 'unknown';

      const placementLabelMap = {
        onSite: t('inventory.onSite'),
        plantInventory: t('inventory.plantInventory'),
        customerInventory: t('inventory.customerInventory'),
        inventoryLocation: t('inventory.inventory'),
        unknown: t('general.unknown'),
      };

      let locationLink = null;
      if (hasSite && loc.site.id && loc.site.primary_site_group?.id) {
        locationLink = `/c/${loc.site.primary_site_group.customer?.id}/plant/${loc.site.primary_site_group.id}/structure/${loc.site.id}`;
      } else if (hasSiteGroup) {
        locationLink = `/c/${loc.site_group.customer?.id}/plant/${loc.site_group.id}/inventory`;
      } else if (hasCustomer) {
        locationLink = `/c/${loc.customer.id}/inventory`;
      }

      const locationLabel = hasSite
        ? loc.site.name
        : hasSiteGroup
          ? refLabel(loc.site_group)
          : hasCustomer
            ? loc.customer.name
            : hasInventoryLocation
              ? loc.inventory_location.name
              : '';

      return {
        ...row,
        placementType,
        placementLabel: placementLabelMap[placementType],
        locationLabel,
        locationLink,
      };
    });
  };
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

function PrepaidCoverageGrid({
  customerId = null,
  onCustomerChange = null,
  statusFilter: statusFilterProp,
  onStatusFilterChange: onStatusFilterChangeProp,
  onDataChange,
}) {
  const { user } = useUser();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const dataGridConfig = useDataGridConfig();
  const [createPrepaidCoveragePlan] = useCreatePrepaidCoveragePlanMutation();

  // Stable ref to the EntityGrid refresh fn, captured via onGridReady.
  const { onGridReady: captureGridReady, refresh: refreshGrid } = useGridRefreshRef();

  // Own the grid apiRef so the ScopeSearchInput search slot can drive the
  // quick filter (dashboard mode).
  const apiRef = useGridApiRef();
  const [gridReady, setGridReady] = useState(false);
  const [searchText, setSearchText, commitSearch] = useGridQuickFilter(apiRef, gridReady);

  // Display label for the applied customer scope chip. A deep-linked
  // ?customer= arrives as a bare id, so the name resolves lazily.
  const [customerScopeLabel, setCustomerScopeLabel] = useState(null);
  useEffect(() => {
    if (onCustomerChange == null || !customerId || customerScopeLabel != null) return;
    let cancelled = false;
    fetchCustomer(customerId)
      .then(customer => {
        if (!cancelled) setCustomerScopeLabel(customer.name);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [onCustomerChange, customerId, customerScopeLabel]);

  const [sellers, setSellers] = useState([]);
  useEffect(() => {
    dispatch(fetchAllUserSellers()).then(res => setSellers(res || []));
  }, [dispatch]);

  const STATUS_OPTIONS = useMemo(() => getStatusOptions(t), [t]);
  const OWNERSHIP_LABELS = useMemo(() => getOwnershipLabels(t), [t]);
  const SOURCE_LABELS = useMemo(() => getSourceLabels(t), [t]);

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        prepaid_service_id: Yup.number().required(t('billing.prepaidCoverage.prepaidServiceRequired')),
        start_date: Yup.date().nullable(),
        term_months: Yup.number()
          .nullable()
          .when('_billingTermMonths', {
            is: val => !val,
            then: schema =>
              schema.required(t('billing.prepaidCoverage.termMonthsRequired', 'Number of months is required')).min(1),
          }),
      }),
    [t],
  );

  // Support both controlled (from parent) and uncontrolled (internal) filter state.
  const [internalStatusFilter, setInternalStatusFilter] = useState('');
  const statusFilter = statusFilterProp !== undefined ? statusFilterProp : internalStatusFilter;
  const setStatusFilter = onStatusFilterChangeProp || setInternalStatusFilter;

  const [placementFilter, setPlacementFilter] = useState('');

  // Dialog open/close state.
  const [renewalDialogOpen, setRenewalDialogOpen] = useState(false);
  const [renewalPreview, setRenewalPreview] = useState(null);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [newOrderSaleId, setNewOrderSaleId] = useState(null);

  // Row data captured at the moment a dialog button is clicked inside selectionActions.
  // Must be state (not derived) because the selection callback closes over the current
  // selection, and the dialog needs to read rows after the selection bar unmounts.
  const [editCoveragesData, setEditCoveragesData] = useState([]);
  const [cancelRadiosData, setCancelRadiosData] = useState([]);
  const [renewalRowsData, setRenewalRowsData] = useState([]);

  const PLACEMENT_OPTIONS = useMemo(
    () => [
      { value: '', label: t('billing.prepaidCoverage.placementAll') },
      { value: 'onSite', label: t('inventory.onSite') },
      { value: 'notInstalled', label: t('inventory.notInstalled') },
    ],
    [t],
  );

  const [columnVisibilityModel, setColumnVisibilityModel] = useUserPreference(
    useGridPreferenceKey('prepaidCoverageGrid', 'columnVisibility.v2'),
    {},
  );

  // Fixed-scope mode (the account Pre-Paid tab, which omits onCustomerChange)
  // is always one customer's rows, so customer is redundant there.
  const isFixedScope = onCustomerChange == null && customerId != null;

  // Stable identity — MUI's columnVisibilityModel prop-sync keys off reference
  // equality, so a fresh model object every render forces a columns-state
  // rebuild per parent render. The fixed-scope default is seeded first and
  // the persisted model spread last, so a user who manually re-shows
  // customer has that choice stick instead of being reset every render.
  const columnVisibility = useMemo(
    () => ({
      model: {
        ...(isFixedScope ? { customer: false } : {}),
        ...columnVisibilityModel,
      },
      onChange: setColumnVisibilityModel,
    }),
    [isFixedScope, columnVisibilityModel, setColumnVisibilityModel],
  );

  // ---------------------------------------------------------------------------
  // Data
  // ---------------------------------------------------------------------------

  const normalizeRows = useMemo(() => buildNormalizeRows(t), [t]);

  // extraParams feeds EntityGrid's default JSON:API serializer, so plain keys
  // (coverage_status, placement, etc.) become filter[...] on the wire.
  const extraParams = useMemo(
    () => ({
      ...(customerId ? { customer_id: customerId } : {}),
      ...(statusFilter ? { coverage_status: statusFilter } : {}),
      ...(placementFilter ? { placement: placementFilter } : {}),
    }),
    [customerId, statusFilter, placementFilter],
  );

  // Custom fetchFn: calls the odl-app endpoint and normalizes rows client-side
  // to compute placementLabel, locationLabel, locationLink from the location obj.
  // The params arrive already serialized by paramSerializer (toJsonApiParams via
  // DataGridConfigProvider → EntityGrid → useServerDataGrid).
  const fetchFn = useCallback(
    async params => {
      const body = await fetchPrepaidCoveragesGrid(params);
      const rows = body?.data ?? [];
      const count = body?.meta?.total ?? rows.length;
      return { results: normalizeRows(rows), count };
    },
    [normalizeRows],
  );

  const handleGridReady = useCallback(
    api => {
      captureGridReady(api);
      setGridReady(true);
    },
    [captureGridReady],
  );

  // ---------------------------------------------------------------------------
  // Dialog success handler
  // ---------------------------------------------------------------------------

  // After any mutation, refresh the grid (recreates dataSource → clears cache and
  // triggers page-0 refetch; DataGrid's ServerDataGrid also clears selection).
  const handleDialogSuccess = useCallback(() => {
    refreshGrid();
    onDataChange?.();
  }, [onDataChange, refreshGrid]);

  // ---------------------------------------------------------------------------
  // Renew submit
  // ---------------------------------------------------------------------------

  const handleRenewSubmit = useCallback(
    async (values, { setSubmitting }) => {
      setSubmitting(true);
      try {
        const accountIds = new Set(renewalRowsData.map(row => row.account_id));
        if (accountIds.size > 1) {
          dispatch(addToast({ message: t('billing.prepaidCoverage.sameBillingAccountError'), level: 'error' }));
          setSubmitting(false);
          return;
        }

        const radioIds = renewalRowsData.map(row => row.radio_id);
        const payload = {
          account_id: renewalRowsData[0]?.account_id,
          site_group_id: values.site_group_id || null,
          prepaid_service_id: values.prepaid_service_id,
          start_date: values.start_date instanceof Date ? values.start_date.toISOString().split('T')[0] : null,
          radio_ids: radioIds,
          salesperson_id: values.salesperson_id || null,
        };
        if (values.term_months) {
          payload.term_months = values.term_months;
        }
        const result = await createPrepaidCoveragePlan({ body: payload });
        if (result.error) throw result.error;
        setNewOrderSaleId(result.data?.order_sale_id);
        refreshGrid();
        onDataChange?.();
      } catch (error) {
        console.error('Error submitting renewal:', error);
        dispatch(addToast({ message: t('billing.prepaidCoverage.renewFailed'), level: 'error' }));
      }
      setSubmitting(false);
    },
    [renewalRowsData, dispatch, onDataChange, t, createPrepaidCoveragePlan, refreshGrid],
  );

  // ---------------------------------------------------------------------------
  // Export — pages through the entire filtered set server-side
  // ---------------------------------------------------------------------------

  // Placement and location are derived client-side from each row's nested
  // location object (see normalizeRows), so the descriptor owns no column for
  // them and they ride along as extra columns. The coverage dates stay in the
  // ISO form the API sends: this file is read by billing spreadsheets, where a
  // localized date is a parsing problem rather than a nicety. Ownership and
  // source read through the same label maps their cells render with.
  const exportOptions = useMemo(
    () => ({
      fileName: 'prepaid-radios.csv',
      columns: {
        start_date: { valueGetter: row => row.start_date || '' },
        end_date: { valueGetter: row => row.end_date || '' },
        ownership_mode: { valueGetter: row => OWNERSHIP_LABELS[row.ownership_mode] || row.ownership_mode || '' },
        source_type: { valueGetter: row => SOURCE_LABELS[row.source_type] || row.source_type || '' },
      },
      extraColumns: [
        {
          field: 'placementLabel',
          headerName: t('billing.prepaidCoverage.columnPlacement'),
          valueGetter: row => row.placementLabel || '',
        },
        {
          field: 'locationLabel',
          headerName: t('billing.prepaidCoverage.columnLocation'),
          valueGetter: row => row.locationLabel || '',
        },
      ],
    }),
    [t, OWNERSHIP_LABELS, SOURCE_LABELS],
  );

  // ---------------------------------------------------------------------------
  // Render overrides on top of the descriptor columns
  // ---------------------------------------------------------------------------

  const fieldOverrides = useMemo(
    () => ({
      customer: {
        renderCell: ({ value }) => <ReferenceChip value={value} resolveReference={dataGridConfig.resolveReference} />,
      },
      iccid: {
        renderCell: params => {
          const label = params.value || params.row.mac_address;
          return (
            <Button component={Link} to={`/support/radios/${params.row.radio_id}/`} size="small">
              {label}
            </Button>
          );
        },
      },
      coverage_status: {
        renderCell: params => {
          const status = params.value;
          if (!status) return null;
          const color = STATUS_CHIP_COLOR[status] || 'neutral';
          const label = status.charAt(0).toUpperCase() + status.slice(1);
          return <GridStatusCell color={color} label={label} />;
        },
      },
      start_date: {
        renderCell: params =>
          params.value ? formatDate(params.row.start_date, user) : t('billing.prepaidCoverage.notAvailable'),
      },
      end_date: {
        renderCell: params =>
          params.value ? formatDate(params.row.end_date, user) : t('billing.prepaidCoverage.notAvailable'),
      },
      updated_at: {
        renderCell: params => {
          if (!params.value) return '';
          const dateStr = new Date(params.value).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          });
          const byName = params.row.updated_by_name;
          return byName ? t('billing.prepaidCoverage.modifiedBy', { date: dateStr, name: byName }) : dateStr;
        },
      },
      ownership_mode: {
        renderCell: params => OWNERSHIP_LABELS[params.value] || params.value || '',
      },
      source_type: {
        renderCell: params => SOURCE_LABELS[params.value] || params.value || '',
      },
      // Computed fields from normalizeRows — rendered if the descriptor includes them.
      placementLabel: {
        renderCell: params => {
          if (!params.value) return null;
          const colorMap = { onSite: 'success', plantInventory: 'warning', customerInventory: 'secondary' };
          return <Chip size="small" color={colorMap[params.row.placementType] || 'info'} label={params.value} />;
        },
      },
      locationLabel: {
        renderCell: params => {
          if (!params.row.locationLink) return params.value;
          return (
            <Button component={Link} size="small" to={params.row.locationLink} sx={{ justifyContent: 'flex-start' }}>
              <Typography variant="caption" noWrap>
                {params.value}
              </Typography>
            </Button>
          );
        },
      },
    }),
    [user, t, OWNERSHIP_LABELS, SOURCE_LABELS, dataGridConfig.resolveReference],
  );

  // ---------------------------------------------------------------------------
  // Toolbar
  // ---------------------------------------------------------------------------

  const quickFilters = useMemo(
    () => [
      {
        key: 'status',
        label: t('billing.prepaidCoverage.statusLabel', 'Status'),
        type: 'single',
        allLabel: STATUS_OPTIONS[0].label,
        options: STATUS_OPTIONS.slice(1),
        value: statusFilter || null,
        onChange: value => setStatusFilter(value ?? ''),
      },
      {
        key: 'placement',
        label: t('billing.prepaidCoverage.placementLabel', 'Placement'),
        type: 'single',
        allLabel: PLACEMENT_OPTIONS[0].label,
        options: PLACEMENT_OPTIONS.slice(1),
        value: placementFilter || null,
        onChange: value => setPlacementFilter(value ?? ''),
      },
    ],
    [t, statusFilter, placementFilter, STATUS_OPTIONS, PLACEMENT_OPTIONS, setStatusFilter],
  );

  const handleResetAll = useCallback(() => {
    setStatusFilter('');
    setPlacementFilter('');
    if (onCustomerChange != null) {
      onCustomerChange(null);
      setCustomerScopeLabel(null);
    }
  }, [setStatusFilter, onCustomerChange]);

  const toolbarContent = <FilterBar quickFilters={quickFilters} onResetAll={handleResetAll} />;

  // Dashboard mode: typed scope search — suggestions apply the customer
  // filter, which then rides in the input as a removable chip. Fixed-scope
  // hosts (the account subview) keep EntityGrid's built-in search.
  const scopeChips = useMemo(() => {
    if (onCustomerChange == null || !customerId) return [];
    return [
      customerScopeChip({
        label: t('general.customer'),
        displayValue: customerScopeLabel ?? `#${customerId}`,
        onRemove: () => {
          onCustomerChange(null);
          setCustomerScopeLabel(null);
        },
      }),
    ];
  }, [onCustomerChange, customerId, customerScopeLabel, t]);

  const scopeProviders = useMemo(() => {
    if (onCustomerChange == null) return [];
    return [
      customerScopeProvider({
        label: t('general.customer'),
        isActive: () => Boolean(customerId),
        applyValue: option => {
          onCustomerChange(option?.id ?? null);
          setCustomerScopeLabel(option?.label ?? null);
        },
      }),
    ];
  }, [onCustomerChange, customerId, t]);

  const searchSlot =
    onCustomerChange != null ? (
      <ScopeSearchInput
        value={searchText}
        onValueChange={setSearchText}
        chips={scopeChips}
        providers={scopeProviders}
        placeholder={t('billing.prepaidCoverage.searchPlaceholder')}
        onSubmit={commitSearch}
      />
    ) : undefined;

  // ---------------------------------------------------------------------------
  // Selection actions (Convert / Edit / Renew)
  // ---------------------------------------------------------------------------

  const selectionActions = useCallback(
    (selection, _clearSelection, apiRef) => {
      const { ids: selectedIds } = selection;
      const toRow = id => apiRef.current?.getRow?.(id);
      const rows = selectedIds.map(toRow).filter(Boolean);
      const count = rows.length;
      const actionSx = { textTransform: 'none' };

      return (
        <>
          <Button
            size="small"
            variant="contained"
            color="info"
            disabled={count === 0}
            startIcon={<SwapHorizIcon />}
            sx={actionSx}
            onClick={() => {
              setCancelRadiosData(
                rows.map(row => ({
                  radio_id: row.radio_id,
                  coverage_id: row.id,
                  label: row.iccid || row.mac_address,
                })),
              );
              setConvertDialogOpen(true);
            }}
          >
            {t('actions.convert')}
          </Button>
          <Button
            size="small"
            variant="contained"
            color="secondary"
            disabled={count === 0}
            startIcon={<EditIcon />}
            sx={actionSx}
            onClick={() => {
              setEditCoveragesData(
                rows.map(row => ({
                  id: row.id,
                  start_date: row.start_date,
                  end_date: row.end_date,
                  ownership_mode: row.ownership_mode,
                  billing_account_id: row.account_id,
                  customer_name: refLabel(row.customer),
                  label: row.iccid || row.mac_address,
                })),
              );
              setEditDialogOpen(true);
            }}
          >
            {t('actions.edit')}
          </Button>
          <Button
            size="small"
            variant="contained"
            color="primary"
            disabled={count === 0}
            sx={actionSx}
            onClick={() => {
              setRenewalRowsData(rows);
              setRenewalDialogOpen(true);
            }}
          >
            {t('actions.renew')}
          </Button>
        </>
      );
    },
    [t],
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <SalesProvider>
      <ConvertCoverageDialog
        open={convertDialogOpen}
        onClose={() => setConvertDialogOpen(false)}
        radios={cancelRadiosData}
        onSuccess={handleDialogSuccess}
      />

      {/* Renewal Dialog */}
      <Dialog
        open={renewalDialogOpen}
        onClose={() => {
          setRenewalDialogOpen(false);
          setNewOrderSaleId(null);
          setRenewalPreview(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('billing.prepaidCoverage.renewDialogTitle')}</DialogTitle>
        <Formik
          initialValues={{ prepaid_service_id: '', start_date: null, salesperson_id: user?.id || '' }}
          validationSchema={validationSchema}
          onSubmit={handleRenewSubmit}
        >
          {newOrderSaleId ? (
            <>
              <DialogContent>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <AlertTitle>{t('billing.prepaidCoverage.renewalOrderCreatedTitle')}</AlertTitle>
                  {t('billing.prepaidCoverage.renewalOrderCreatedBody')}
                </Alert>
                <Typography>{t('billing.prepaidCoverage.viewOrderHere')}</Typography>
                <Box sx={{ mt: 2 }}>
                  <Button
                    component={Link}
                    to={`/sales/orders/${newOrderSaleId}/`}
                    target="_blank"
                    variant="outlined"
                    startIcon={<OpenInNewIcon />}
                  >
                    {t('billing.prepaidCoverage.orderSaleLink', { id: newOrderSaleId })}
                  </Button>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  <strong>{t('billing.prepaidCoverage.nextSteps')}</strong>
                  <br />
                  {t('billing.prepaidCoverage.renewalStep1')}
                  <br />
                  {t('billing.prepaidCoverage.renewalStep2')}
                  <br />
                  {t('billing.prepaidCoverage.renewalStep3')}
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => {
                    setRenewalDialogOpen(false);
                    setNewOrderSaleId(null);
                    setRenewalPreview(null);
                  }}
                  color="primary"
                  variant="contained"
                >
                  {t('actions.close')}
                </Button>
              </DialogActions>
            </>
          ) : renewalPreview ? (
            ({ isSubmitting, submitForm }) => (
              <>
                <DialogContent>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {t('billing.prepaidCoverage.renewPreviewMessage')}
                  </Alert>
                  <Stack spacing={1.5}>
                    <Typography variant="body2">
                      <strong>{t('billing.prepaidCoverage.prepaidService')}:</strong> {renewalPreview.productLabel}
                    </Typography>
                    <Typography variant="body2">
                      <strong>{t('billing.prepaidCoverage.billingStartDate')}:</strong>{' '}
                      {renewalPreview.startDate
                        ? formatDate(renewalPreview.startDate.toISOString().split('T')[0], user)
                        : t('billing.prepaidCoverage.immediate')}
                    </Typography>
                    {renewalPreview.billingTermMonths && (
                      <Typography variant="body2">
                        <strong>{t('billing.prepaidCoverage.billingEndDate', 'End Date')}:</strong>{' '}
                        {formatDate(
                          addMonths(renewalPreview.startDate || new Date(), renewalPreview.billingTermMonths)
                            .toISOString()
                            .split('T')[0],
                          user,
                        )}{' '}
                        ({renewalPreview.billingTermMonths} {t('general.months', 'months')})
                      </Typography>
                    )}
                    {renewalPreview.salespersonName && (
                      <Typography variant="body2">
                        <strong>{t('warehouse.salesPerson')}:</strong> {renewalPreview.salespersonName}
                      </Typography>
                    )}
                    <Typography variant="body2">
                      <strong>{t('billing.prepaidCoverage.radiosCount', { count: renewalRowsData.length })}:</strong>
                    </Typography>
                    <ul>
                      {renewalRowsData.map(row => (
                        <li key={row.id}>{row.iccid ?? row.mac_address}</li>
                      ))}
                    </ul>
                  </Stack>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setRenewalPreview(null)} color="secondary">
                    {t('actions.back')}
                  </Button>
                  <Button color="primary" variant="contained" disabled={isSubmitting} onClick={submitForm}>
                    {t('actions.confirm')}
                  </Button>
                </DialogActions>
              </>
            )
          ) : (
            ({ values, errors, touched, setFieldValue, validateForm }) => (
              <Form>
                <DialogContent>
                  <Stack direction="column" spacing={2} sx={{ mb: 3 }}>
                    <ProductAutocomplete
                      fullWidth
                      required
                      label={t('billing.prepaidCoverage.prepaidService')}
                      name="prepaid_service_id"
                      size="small"
                      value={values.prepaid_service_id}
                      filter={p => p.is_service}
                      helperText={t('billing.prepaidCoverage.prepaidServiceHelperText')}
                      onChange={p => {
                        setFieldValue('prepaid_service_id', p.id);
                        setFieldValue('_productLabel', `${p.item_part_num}: ${p.item_name}`);
                        setFieldValue('_billingTermMonths', p.billing_term_months);
                      }}
                    />
                    <DatePicker
                      label={t('billing.prepaidCoverage.billingStartDate')}
                      value={values.start_date}
                      onChange={val => setFieldValue('start_date', val)}
                      slotProps={{
                        textField: {
                          size: 'small',
                          fullWidth: true,
                          error: touched.start_date && Boolean(errors.start_date),
                          helperText:
                            (touched.start_date && errors.start_date) ||
                            t('billing.prepaidCoverage.billingStartDateHelper'),
                        },
                      }}
                    />
                    {!values._billingTermMonths && values.prepaid_service_id && (
                      <TextField
                        label={t('general.months', 'Months')}
                        type="number"
                        size="small"
                        fullWidth
                        required
                        value={values.term_months || ''}
                        onChange={e => setFieldValue('term_months', e.target.value ? Number(e.target.value) : '')}
                        error={touched.term_months && Boolean(errors.term_months)}
                        helperText={
                          (touched.term_months && errors.term_months) ||
                          t(
                            'billing.prepaidCoverage.dynamicTermHelper',
                            'Enter the number of months for this prepaid term',
                          )
                        }
                        inputProps={{ min: 1 }}
                      />
                    )}
                    <UserAutoComplete
                      name="salesperson_id"
                      label={t('warehouse.salesPerson')}
                      users={sellers}
                      value={values.salesperson_id}
                      fullWidth
                      size="small"
                      onChange={(e, val) => setFieldValue('salesperson_id', val)}
                    />
                  </Stack>
                  <Typography variant="caption">{t('billing.prepaidCoverage.renewingForRadios')}</Typography>
                  <ul>
                    {renewalRowsData.map(row => (
                      <li key={row.id}>{row.iccid ?? row.mac_address}</li>
                    ))}
                  </ul>
                </DialogContent>
                <DialogActions>
                  <Button
                    onClick={() => {
                      setNewOrderSaleId(null);
                      setRenewalPreview(null);
                      setRenewalDialogOpen(false);
                    }}
                    color="secondary"
                  >
                    {t('actions.cancel')}
                  </Button>
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={async () => {
                      const errs = await validateForm();
                      if (Object.keys(errs).length > 0) return;
                      const salesperson = sellers.find(s => s.id === values.salesperson_id);
                      setRenewalPreview({
                        productLabel: values._productLabel || values.prepaid_service_id,
                        startDate: values.start_date,
                        billingTermMonths: values._billingTermMonths ?? values.term_months,
                        salespersonName: salesperson ? `${salesperson.first_name} ${salesperson.last_name}` : null,
                      });
                    }}
                  >
                    {t('actions.review')}
                  </Button>
                </DialogActions>
              </Form>
            )
          )}
        </Formik>
      </Dialog>

      <EditCoverageDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        coverages={editCoveragesData}
        onSuccess={handleDialogSuccess}
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, width: '100%' }}>
        <EntityGrid
          basePath="prepaid-coverages"
          fetchFn={fetchFn}
          fieldOverrides={fieldOverrides}
          extraParams={extraParams}
          options={{
            searchPlaceholder: t('billing.prepaidCoverage.searchPlaceholder'),
            searchSlot,
            persistKey: 'prepaidCoverageGrid',
            infiniteScroll: true,
            toolbarContent,
            export: exportOptions,
            // Standalone dashboard gets the elevated toolbar surface; the
            // account subview's grid sits inside the stage card and stays flat.
            toolbarVariant: onCustomerChange != null ? 'elevated' : 'flat',
            selection: {
              enabled: true,
              actions: selectionActions,
            },
            columnVisibility,
            gridApiRef: apiRef,
            onGridReady: handleGridReady,
            gridProps: {
              density: 'compact',
              sx: { flex: 1 },
            },
          }}
        />
      </Box>
    </SalesProvider>
  );
}

export default PrepaidCoverageGrid;
